import { useState, useEffect, useRef, useCallback, type ChangeEvent, type DragEvent, type FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import './ApontamentoForm.css';

interface PreviewFile {
  id: string;
  file: File;
  previewUrl: string;
}

interface ApontamentoHistorico {
  id: string;
  percentual: number;
  registrado_em: string;
  assistente_nome: string;
}

const MAX_FILES = 20;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png'];

function ApontamentoForm() {
  const { id: atividadeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [percentual, setPercentual] = useState(0);
  const [nomeAtividade, setNomeAtividade] = useState('');
  const [historico, setHistorico] = useState<ApontamentoHistorico[]>([]);
  const [files, setFiles] = useState<PreviewFile[]>([]);
  const [errors, setErrors] = useState<{ fotos?: string; api?: string }>({});
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load current activity data and history
  useEffect(() => {
    async function loadData() {
      try {
        setLoadingInfo(true);
        const [apontamentos] = await Promise.all([
          api.get(`/atividades/${atividadeId}/apontamentos`),
        ]);
        setHistorico(apontamentos || []);
        // Set percentual to the latest value if exists
        if (apontamentos && apontamentos.length > 0) {
          setPercentual(apontamentos[0].percentual);
          setNomeAtividade(apontamentos[0].atividade_nome || '');
        }
      } catch {
        // If no history, start at 0
      } finally {
        setLoadingInfo(false);
      }
    }
    if (atividadeId) loadData();
  }, [atividadeId]);

  const validateFiles = (newFiles: File[]): { valid: File[]; error?: string } => {
    const validFiles: File[] = [];
    const totalAfter = files.length + newFiles.length;

    if (totalAfter > MAX_FILES) {
      return { valid: [], error: `Máximo de ${MAX_FILES} fotos por apontamento.` };
    }

    for (const file of newFiles) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        return { valid: [], error: 'Apenas arquivos JPG e PNG são permitidos.' };
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        return { valid: [], error: `Cada arquivo deve ter no máximo ${MAX_FILE_SIZE_MB}MB.` };
      }
      validFiles.push(file);
    }

    return { valid: validFiles };
  };

  const addFiles = useCallback(
    (newFiles: File[]) => {
      const { valid, error } = validateFiles(newFiles);
      if (error) {
        setErrors({ fotos: error });
        return;
      }
      setErrors({});

      const newPreviews: PreviewFile[] = valid.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        previewUrl: URL.createObjectURL(file),
      }));

      setFiles((prev) => [...prev, ...newPreviews]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [files.length]
  );

  const removeFile = (id: string) => {
    setFiles((prev) => {
      const removed = prev.find((f) => f.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
    setErrors({});
  };

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handlePercentualChange = (value: number) => {
    const clamped = Math.min(100, Math.max(0, Math.round(value)));
    setPercentual(clamped);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (files.length === 0) {
      setErrors({ fotos: 'Pelo menos 1 foto é obrigatória.' });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setErrors({});

    try {
      // Step 1: Create apontamento (JSON)
      setUploadProgress(10);
      const apontamento = await api.post(`/atividades/${atividadeId}/apontamentos`, { percentual });

      // Step 2: Upload photos (all at once, field name 'fotos')
      setUploadProgress(30);
      const photoFormData = new FormData();
      for (const f of files) {
        photoFormData.append('fotos', f.file);
      }
      await api.post(`/apontamentos/${apontamento.apontamento.id}/fotos`, photoFormData);
      setUploadProgress(100);

      navigate(-1);
    } catch (err: unknown) {
      setErrors({ api: err instanceof Error ? err.message : 'Erro ao enviar apontamento' });
      setUploading(false);
    }
  };

  return (
    <div className="apontamento-form-page">
      <div className="apontamento-form-container">
        <a href="#" onClick={(e) => { e.preventDefault(); navigate(-1); }} className="apontamento-form-back">
          ← Voltar
        </a>

        <header className="apontamento-form-header">
          <h1>Novo Apontamento</h1>
          <p className="apontamento-form-subtitle">
            Registre o progresso da atividade com fotos
          </p>
        </header>

        <form className="apontamento-form-card" onSubmit={handleSubmit} noValidate>
          <h2>Registrar Apontamento</h2>

          {errors.api && (
            <p style={{ color: '#ff6b6b', marginBottom: '1rem' }}>{errors.api}</p>
          )}

          {/* Percentual Slider */}
          <div className="percentual-section">
            <label htmlFor="percentual-slider">Percentual de Conclusão</label>
            <div className="percentual-controls">
              <input
                id="percentual-slider"
                type="range"
                min={0}
                max={100}
                value={percentual}
                onChange={(e) => handlePercentualChange(Number(e.target.value))}
                className="percentual-slider"
              />
              <input
                type="number"
                min={0}
                max={100}
                value={percentual}
                onChange={(e) => handlePercentualChange(Number(e.target.value))}
                className="percentual-input"
                aria-label="Percentual numérico"
              />
              <span style={{ color: '#999', fontSize: '0.9rem' }}>%</span>
            </div>
          </div>

          {/* Photo Upload */}
          <div className="upload-section">
            <label>Fotos (mín. 1, máx. {MAX_FILES})</label>

            <div
              className={`upload-dropzone ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              aria-label="Área de upload de fotos. Clique ou arraste arquivos."
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
            >
              <span className="upload-dropzone-icon" aria-hidden="true">📸</span>
              <span className="upload-dropzone-text">
                Arraste fotos aqui ou clique para selecionar
              </span>
              <span className="upload-dropzone-hint">
                JPG ou PNG, até {MAX_FILE_SIZE_MB}MB cada
              </span>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              multiple
              onChange={handleFileInput}
              className="upload-file-input"
              aria-hidden="true"
            />

            {errors.fotos && <span className="form-error">{errors.fotos}</span>}

            {/* Preview Grid */}
            {files.length > 0 && (
              <div className="upload-previews">
                {files.map((preview) => (
                  <div className="upload-preview-item" key={preview.id}>
                    <img src={preview.previewUrl} alt={preview.file.name} />
                    <button
                      type="button"
                      className="upload-preview-remove"
                      onClick={() => removeFile(preview.id)}
                      aria-label={`Remover ${preview.file.name}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {files.length > 0 && (
              <p style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.5rem' }}>
                {files.length} de {MAX_FILES} fotos selecionadas
              </p>
            )}
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="upload-progress">
              <div className="upload-progress-bar">
                <div
                  className="upload-progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <span className="upload-progress-text">Enviando... {uploadProgress}%</span>
            </div>
          )}

          {/* Submit */}
          <div className="apontamento-submit-section">
            <button type="submit" className="btn-primary" disabled={uploading}>
              {uploading ? 'Enviando...' : 'Enviar Apontamento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ApontamentoForm;
